import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const formatToISO = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseToDateArray = (val) => {
  if (!val) return [];
  if (typeof val === 'string') {
    return val
      .split(',')
      .map((v) => new Date(v))
      .filter((d) => !isNaN(d));
  }
  return [];
};

const CustomMultiDatePicker = ({ label, name, value = '', onChange, placeholder, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selectedDates = parseToDateArray(value);

  // Handle click on date (toggle add/remove)
  const handleDateChange = (date) => {
    if (!date) return;

    const iso = formatToISO(date);
    let updatedDates;

    if (selectedDates.some((d) => formatToISO(d) === iso)) {
      updatedDates = selectedDates.filter((d) => formatToISO(d) !== iso);
    } else {
      updatedDates = [...selectedDates, date];
    }

    onChange({
      target: {
        name,
        value: updatedDates.map((d) => formatToISO(d)).join(','),
      },
    });
  };

  // Close picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle Enter key to close picker
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setIsOpen(false);
    }
  };

  // Display selected dates in input
  const displayValue = selectedDates.map((d) => d.toLocaleDateString()).join(', ');

  return (
    <div className="mb-4 w-full" ref={wrapperRef}>
      {label && (
        <label htmlFor={name} className="block text-md text-gray-700 font-semibold mb-1">
          {label}
        </label>
      )}
      <input
        type="text"
        readOnly
        value={displayValue}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Select multiple dates'}
        className={`w-full px-4 py-2 border rounded-lg shadow-sm text-gray-900 focus:outline-none focus:ring-2
          ${isOpen ? 'focus:ring-blue-500 border-blue-500' : 'border-gray-700'}
          ${error ? 'border-red-500' : ''}
          transition-all duration-200
        `}
      />

      {/* Calendar Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1">
          <DatePicker
            selected={null} // controlled manually
            onChange={handleDateChange}
            highlightDates={selectedDates}
            inline
          />
        </div>
      )}

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default CustomMultiDatePicker;
